import { relativeSeat } from "@guandan/protocol";
import type { Seat } from "@guandan/engine";

/**
 * Where everything sits on the table.
 *
 * One implementation for both shapes. The viewer is always at the bottom, their
 * partner across, opponents left and right — the arrangement a player expects
 * from sitting down at a real table, and the reason seat numbers never appear
 * in the layout maths: everything is relative to who is looking.
 *
 * Pure, so it can be tested at every size without a browser.
 */

export type LayoutMode = "portrait" | "desktop";

export interface SeatBox {
  readonly seat: Seat;
  /** 0 = viewer, 1 = left, 2 = across, 3 = right. */
  readonly relative: 0 | 1 | 2 | 3;
  /** Centre, in pixels within the table area. */
  readonly x: number;
  readonly y: number;
  /** Where this seat's cards fly to when played. */
  readonly playX: number;
  readonly playY: number;
  readonly avatarSize: number;
}

export interface TableLayout {
  readonly mode: LayoutMode;
  readonly width: number;
  readonly height: number;
  readonly seats: readonly SeatBox[];
  /** Middle of the table, where the current trick sits. */
  readonly trickX: number;
  readonly trickY: number;
  /** Geometry of the viewer's own fanned hand. */
  readonly fan: FanGeometry;
  readonly cardWidth: number;
  readonly cardHeight: number;
  /**
   * Cards in the trick are drawn smaller than cards in hand.
   *
   * A six-card combo laid out at hand size is wider than a phone, and it
   * collides with the seats on either side. They are there to be read, not
   * handled, so they can afford to be smaller.
   */
  readonly trickCardWidth: number;
  readonly trickCardHeight: number;
}

export type ReplayHandDirection = "horizontal" | "vertical";

/** A bounded hand area used by the omniscient replay viewer. */
export interface ReplayLane {
  readonly relative: 0 | 1 | 2 | 3;
  readonly direction: ReplayHandDirection;
  readonly columns: 1 | 2;
  /** Card area, in pixels within the replay surface. */
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly columnGap: number;
}

export interface ReplaySafeZone {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

export interface ReplayLayout extends TableLayout {
  readonly replayLanes: readonly ReplayLane[];
  readonly trickSafe: ReplaySafeZone;
}

/** Wide live-table geometry keeps hidden opponent hands compact while giving
 * the viewer's interactive hand enough face area to read and select. */
export interface WideLiveLayout extends ReplayLayout {
  readonly viewerCardWidth: number;
  readonly viewerCardHeight: number;
  readonly viewerFan: FanGeometry;
}

export interface FanGeometry {
  /** Optional fixed row size for a touch-friendly live hand. */
  readonly rowSize?: number;
  readonly rowGap?: number;
  /** Centre of the arc. */
  readonly centreX: number;
  readonly baseY: number;
  /** Maximum horizontal spread between the outermost cards. */
  readonly spread: number;
  /** Degrees of rotation between the outermost cards. */
  readonly arc: number;
  /** How far a selected card lifts. */
  readonly lift: number;
}

export function modeFor(width: number): LayoutMode {
  return width >= 768 ? "desktop" : "portrait";
}

/** Card size that keeps a full hand readable at this width. */
function cardSize(width: number, mode: LayoutMode): { w: number; h: number } {
  const w = mode === "desktop"
    ? Math.min(92, Math.max(64, width * 0.062))
    : Math.min(82, Math.max(58, width * 0.18));
  return { w, h: Math.round(w * 1.4) };
}

/**
 * Seat positions, and the point each seat's cards travel to.
 *
 * Portrait puts the three other players across the top half rather than on a
 * true circle: a phone is far taller than it is wide, and a circle inscribed in
 * it would push the side seats into the hand.
 */
export function layout(width: number, height: number, viewer: Seat | null, modeOverride?: LayoutMode): TableLayout {
  const mode = modeOverride ?? modeFor(width);
  const { w: cardWidth, h: cardHeight } = cardSize(width, mode);

  const trickX = width / 2;
  const trickY = mode === "desktop" ? height * 0.46 : height * 0.42;

  const avatarSize = mode === "desktop" ? 64 : 44;
  const heroAvatar = mode === "desktop" ? 84 : 52;

  const seats: SeatBox[] = ([0, 1, 2, 3] as Seat[]).map((seat) => {
    const relative = relativeSeat(seat, viewer);
    if (mode === "desktop") {
      // A genuine circle, with the viewer enlarged at the bottom.
      const radiusX = Math.min(width * 0.41, width / 2 - heroAvatar * 0.7);
      const radiusY = Math.min(height * 0.34, 300);
      const angles = [90, 180, 270, 0]; // bottom, left, top, right
      const degrees = angles[relative] as number;
      const radians = (degrees * Math.PI) / 180;
      const x = trickX + Math.cos(radians) * radiusX;
      const circularY = trickY + Math.sin(radians) * radiusY;
      const y = relative === 0
        ? Math.min(circularY, height - cardHeight - heroAvatar * 0.65 - 54)
        : circularY;
      return {
        seat, relative, x, y,
        playX: trickX + Math.cos(radians) * radiusX * 0.42,
        playY: trickY + Math.sin(radians) * radiusY * 0.46,
        avatarSize: relative === 0 ? heroAvatar : avatarSize,
      };
    }

    // Side seats sit high and hard against the edges, clear of the trick.
    const positions: Record<0 | 1 | 2 | 3, { x: number; y: number }> = {
      0: { x: trickX, y: height * 0.70 },
      1: { x: width * 0.11, y: height * 0.26 },
      2: { x: trickX, y: height * 0.10 },
      3: { x: width * 0.89, y: height * 0.26 },
    };
    const at = positions[relative];
    return {
      seat, relative, x: at.x, y: at.y,
      playX: trickX + (at.x - trickX) * 0.42,
      playY: trickY + (at.y - trickY) * 0.44,
      avatarSize: relative === 0 ? heroAvatar : avatarSize,
    };
  });

  // Shrink the trick further when the combo would otherwise overrun the width.
  const trickScale = mode === "desktop" ? 0.94 : 0.68;

  return {
    mode, width, height, seats, trickX, trickY, cardWidth, cardHeight,
    trickCardWidth: Math.round(cardWidth * trickScale),
    trickCardHeight: Math.round(cardHeight * trickScale),
    fan: {
      centreX: width / 2,
      // Leave room for the dip at the ends of the arc, or the outermost cards
      // hang off the bottom of the screen.
      baseY: mode === "desktop" ? height - cardHeight * 0.58 : height - cardHeight * 0.56,
      // Desktop has room for a genuinely wide fan; a phone must stay on screen.
      spread: mode === "desktop"
        ? Math.min(width * 0.78, 1120)
        : width - cardWidth * 0.42,
      arc: mode === "desktop" ? 16 : 22,
      lift: mode === "desktop" ? 26 : 20,
    },
  };
}

/** Portrait gameplay gives the local hand two stable rows; replays keep their own geometry. */
export function mobileLiveLayout(width: number, height: number, viewer: Seat | null): TableLayout {
  const base = layout(width, height, viewer, "portrait");
  const cardWidth = Math.max(48, Math.min(62, (width - 32) / 6.4));
  const cardHeight = Math.round(cardWidth * 1.4);
  const fan: FanGeometry = {
    centreX: width / 2, baseY: height - cardHeight / 2 - 12,
    spread: width - 32, arc: 0, lift: 18, rowSize: 14, rowGap: cardHeight * 0.82,
  };
  const heroY = fan.baseY - cardHeight * 1.32 - fan.lift - 56;
  return {
    ...base, cardWidth, cardHeight, fan,
    seats: base.seats.map((seat) => seat.relative === 0
      ? { ...seat, x: width / 2, y: heroY, avatarSize: 48, playX: width / 2, playY: base.trickY + 32 }
      : { ...seat, avatarSize: 40 }),
  };
}

/**
 * Replay-only portrait geometry.
 *
 * A replay shows all four hands, so the live-table geometry leaves too little
 * separation between a player's portrait, their hand lane, and the centre
 * trick. Keep the four seats on a predictable cross and reserve the middle for
 * the action being replayed.
 */
export function replayLayout(width: number, height: number, viewer: Seat | null, forceDesktop: true): ReplayLayout;
export function replayLayout(width: number, height: number, viewer: Seat | null, forceDesktop?: false): TableLayout;
export function replayLayout(width: number, height: number, viewer: Seat | null, forceDesktop: boolean): TableLayout | ReplayLayout;
export function replayLayout(width: number, height: number, viewer: Seat | null, forceDesktop = false): TableLayout | ReplayLayout {
  const base = layout(width, height, viewer, forceDesktop ? "desktop" : undefined);
  if (forceDesktop) {
    // Percent-first geometry keeps the same relationships when the wide shell
    // leaves a compact surface beside the rail or when a public replay fills
    // a much larger monitor. All coordinates below are derived from this
    // surface, never from viewport-specific offsets.
    const trickSafe: ReplaySafeZone = {
      left: width * 0.27,
      top: height * 0.32,
      width: width * 0.46,
      height: height * 0.36,
    };
    const replayLanes: readonly ReplayLane[] = [
      { relative: 0, direction: "horizontal", columns: 1, left: width * 0.33, top: height * 0.72, width: width * 0.34, height: height * 0.18, columnGap: width * 0.012 },
      { relative: 1, direction: "vertical", columns: 2, left: width * 0.145, top: height * 0.14, width: width * 0.12, height: height * 0.72, columnGap: width * 0.012 },
      { relative: 2, direction: "horizontal", columns: 1, left: width * 0.33, top: height * 0.10, width: width * 0.34, height: height * 0.18, columnGap: width * 0.012 },
      { relative: 3, direction: "vertical", columns: 2, left: width * 0.735, top: height * 0.14, width: width * 0.12, height: height * 0.72, columnGap: width * 0.012 },
    ];

    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
    const sideLane = replayLanes[1]!;
    // Two columns of fourteen cards get a minimum 55% card-height step. The
    // side lane participates in sizing so it never falls back to the old,
    // unreadable single stack on a short landscape surface.
    // Round down before deriving the rendered height. CardFace rounds its
    // height too, so this avoids a one-pixel rounding overflow that would
    // otherwise lower the visible step below the intended 55% threshold.
    const sideCardWidth = Math.floor(sideLane.height / (1 + 13 * 0.55) / 1.4);
    const cardWidth = clamp(Math.min(width * 0.048, height * 0.063, sideCardWidth), 28, 64);
    const cardHeight = Math.round(cardWidth * 1.4);
    const trickCardWidth = Math.min(
      cardWidth * 0.94,
      trickSafe.width / 3.4,
      trickSafe.height / 1.4,
    );
    const trickY = trickSafe.top + trickSafe.height / 2;
    const smallAvatar = clamp(Math.min(width * 0.07, height * 0.14), 44, 64);
    const heroAvatar = clamp(Math.min(width * 0.09, height * 0.17), 52, 78);
    const avatarAt: Record<0 | 1 | 2 | 3, { x: number; y: number }> = {
      0: { x: width * 0.72, y: height * 0.81 },
      // Keep the side label stack inside the surface as well as the portrait.
      // The stack is wider than the avatar when a player name is present.
      1: { x: width * 0.09, y: height * 0.50 },
      2: { x: width * 0.28, y: height * 0.19 },
      3: { x: width * 0.91, y: height * 0.50 },
    };
    const seats = base.seats.map((box) => {
      const avatar = avatarAt[box.relative];
      const avatarSize = box.relative === 0 ? heroAvatar : smallAvatar;
      // SeatRing centres the complete avatar/name/count stack. Offset that
      // stack by the derived label footprint so the portrait itself, rather
      // than the stack's bounding box, shares the lane centre.
      const avatarY = avatar.y + avatarSize * 0.28;
      return {
        ...box,
        x: avatar.x,
        y: avatarY,
        playX: trickSafe.left + trickSafe.width / 2 + (avatar.x - (trickSafe.left + trickSafe.width / 2)) * 0.42,
        playY: trickY + (avatarY - trickY) * 0.42,
        avatarSize,
      };
    });

    return {
      ...base,
      seats,
      trickX: trickSafe.left + trickSafe.width / 2,
      trickY,
      cardWidth,
      cardHeight,
      trickCardWidth: Math.round(trickCardWidth),
      trickCardHeight: Math.round(trickCardWidth * 1.4),
      // The live wide table's interactive hand uses the same bottom lane as
      // the replay viewer. Keep its fan API, but make its centre and spread
      // consume that lane instead of the circular live-table coordinates.
      fan: {
        centreX: replayLanes[0]!.left + replayLanes[0]!.width / 2,
        baseY: replayLanes[0]!.top + replayLanes[0]!.height / 2,
        spread: replayLanes[0]!.width,
        arc: 0,
        lift: Math.min(26, cardHeight * 0.55),
      },
      replayLanes,
      trickSafe,
    };
  }
  if (base.mode !== "portrait") return base;

  const trickX = width / 2;
  const trickY = height * 0.47;
  const positions: Record<0 | 1 | 2 | 3, { x: number; y: number; playX: number; playY: number }> = {
    0: { x: width * 0.5, y: height * 0.86, playX: width * 0.5, playY: height * 0.61 },
    1: { x: width * 0.11, y: height * 0.35, playX: width * 0.18, playY: height * 0.45 },
    2: { x: width * 0.5, y: height * 0.11, playX: width * 0.5, playY: height * 0.33 },
    3: { x: width * 0.89, y: height * 0.35, playX: width * 0.82, playY: height * 0.45 },
  };

  return {
    ...base,
    trickX,
    trickY,
    // A replay's centre trick is a focal point, not a second full-size hand.
    trickCardWidth: Math.min(base.trickCardWidth, 46),
    trickCardHeight: Math.min(base.trickCardHeight, 64),
    seats: base.seats.map((box) => {
      const at = positions[box.relative];
      return { ...box, x: at.x, y: at.y, playX: at.playX, playY: at.playY };
    }),
  };
}

/**
 * Live-only wide geometry.
 *
 * Replay needs one card size because all four hands carry equal information.
 * At a live table the three hidden hands are only counts, while the viewer's
 * cards are controls. Keep those concerns separate without changing replay or
 * mobile geometry.
 */
export function wideLiveLayout(width: number, height: number, viewer: Seat | null): WideLiveLayout {
  const replay = replayLayout(width, height, viewer, true) as ReplayLayout;
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  const replayLanes: readonly ReplayLane[] = replay.replayLanes.map((lane) => {
    if (lane.relative === 0) {
      return {
        ...lane,
        left: width * 0.06,
        top: height * 0.75,
        width: width * 0.76,
        height: height * 0.22,
      };
    }
    if (lane.relative === 1 || lane.relative === 3) {
      return { ...lane, top: height * 0.10, height: height * 0.62 };
    }
    return lane;
  });
  const sideLane = replayLanes.find((lane) => lane.relative === 1)!;
  const sideCardWidth = Math.floor(sideLane.height / (1 + 13 * 0.55) / 1.4);
  const cardWidth = clamp(
    Math.min(width * 0.048, height * 0.063, sideCardWidth),
    24,
    64,
  );
  const cardHeight = Math.round(cardWidth * 1.4);
  const viewerCardWidth = clamp(Math.min(width * 0.095, height * 0.11), 44, 96);
  const viewerCardHeight = Math.round(viewerCardWidth * 1.4);
  const viewerLane = replayLanes.find((lane) => lane.relative === 0)!;
  const viewerFan: FanGeometry = {
    centreX: viewerLane.left + viewerLane.width / 2,
    baseY: height * 0.86,
    spread: viewerLane.width,
    arc: 0,
    lift: Math.min(30, viewerCardHeight * 0.34),
  };
  const seats = replay.seats.map((seat) => {
    if (seat.relative === 2) return { ...seat, x: width / 2, y: height * .16, playX: replay.trickX, playY: replay.trickY - height * .12 };
    if (seat.relative !== 0) return seat;
    const x = width * 0.91;
    const y = height * 0.86 + seat.avatarSize * 0.28;
    return {
      ...seat,
      x,
      y,
      playX: replay.trickX + (x - replay.trickX) * 0.42,
      playY: replay.trickY + (y - replay.trickY) * 0.42,
    };
  });

  return {
    ...replay,
    seats,
    replayLanes,
    cardWidth,
    cardHeight,
    viewerCardWidth,
    viewerCardHeight,
    viewerFan,
  };
}

export interface CardPlacement {
  readonly x: number;
  readonly y: number;
  readonly rotation: number;
}

export interface FanHitTestInput {
  readonly x: number;
  readonly y: number;
  readonly count: number;
  readonly fan: FanGeometry;
  readonly cardWidth: number;
  readonly cardHeight: number;
  readonly selected?: ReadonlySet<number>;
  readonly padding?: number;
  /** Browser-resolved topmost card, when the pointer landed on rendered DOM. */
  readonly renderedTargetIndex?: number | null;
}

/**
 * Resolve one pointer position to the nearest visible card in an overlapping
 * hand. Rendering and hit testing deliberately share the same fan geometry;
 * wide live tables use a larger viewer fan than their hidden opponent cards.
 */
export function fanCardAtPoint({
  x, y, count, fan, cardWidth, cardHeight, selected = new Set(), padding = 16,
  renderedTargetIndex = null,
}: FanHitTestInput): number | null {
  if (count <= 0) return null;
  // The rank/suit corner of a heavily-overlapped card can be much closer to a
  // neighbour's centre. DOM hit testing already accounts for paint order,
  // rotation and lifted cards, so preserve that exact visual target.
  if (
    renderedTargetIndex !== null
    && Number.isInteger(renderedTargetIndex)
    && renderedTargetIndex >= 0
    && renderedTargetIndex < count
  ) return renderedTargetIndex;
  let nearest: { index: number; score: number } | null = null;
  for (let index = 0; index < count; index++) {
    const place = fanPlacement(index, count, fan, cardWidth);
    const centreY = place.y - (selected.has(index) ? fan.lift : 0);
    const dx = Math.abs(place.x - x);
    const dy = Math.abs(centreY - y);
    if (dx > cardWidth / 2 + padding || dy > cardHeight / 2 + padding) continue;
    // Horizontal distance owns the choice in a tightly-overlapped fan; the
    // small vertical term distinguishes a lifted card without surprising taps.
    const score = dx + dy * 0.16;
    if (nearest === null || score < nearest.score || (score === nearest.score && index > nearest.index)) {
      nearest = { index, score };
    }
  }
  return nearest?.index ?? null;
}

/**
 * Place one card in the viewer's fan.
 *
 * Cards overlap as the hand grows so a full 27 always fits: the spread is
 * fixed, and the step between cards shrinks to fill it. The slight arc and the
 * matching vertical dip are what stop it reading as a flat row of rectangles.
 */
export function fanPlacement(
  index: number, count: number, fan: FanGeometry, cardWidth: number,
): CardPlacement {
  if (fan.rowSize && count > fan.rowSize) {
    const row = Math.floor(index / fan.rowSize);
    const rows = Math.ceil(count / fan.rowSize);
    return fanPlacement(index % fan.rowSize, Math.min(fan.rowSize, count - row * fan.rowSize), {
      ...fan, rowSize: undefined, baseY: fan.baseY - (rows - 1 - row) * (fan.rowGap ?? 0),
    }, cardWidth);
  }
  if (count <= 1) return { x: fan.centreX, y: fan.baseY, rotation: 0 };

  const usable = Math.max(0, fan.spread - cardWidth);
  const step = Math.min(cardWidth * 0.72, usable / (count - 1));
  const totalWidth = step * (count - 1);
  const x = fan.centreX - totalWidth / 2 + step * index;

  // -1 at the left edge, +1 at the right.
  const t = (index / (count - 1)) * 2 - 1;
  const rotation = (t * fan.arc) / 2;
  // Cards at the edges of the arc sit slightly lower, following the rotation.
  const dip = (1 - Math.cos((t * fan.arc * Math.PI) / 360)) * cardWidth * 1.6;

  return { x, y: fan.baseY + dip, rotation };
}
